import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { FileText, Users, ShoppingBag, UserCheck } from 'lucide-react';

// تبدیل اعداد به فارسی
const toPersianDigits = (str) => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return str.toString().replace(/\d/g, (d) => persianDigits[d]).replace(/\./g, '٫');
};

const fmt = (n) => {
    const num = Number(n) || 0;
    return toPersianDigits(new Intl.NumberFormat('en-US').format(num));
};

export default function CompanyDashboard() {
    const [profile, setProfile] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [profileRes, statsRes] = await Promise.all([
                    apiClient.GET('/api/v1/company/profile'),
                    apiClient.GET('/api/v1/company/profile/stats')
                ]);

                if (profileRes.data) setProfile(profileRes.data);
                if (statsRes.data) setStats(statsRes.data);
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>در حال دریافت اطلاعات داشبورد...</div>;
    }

    // استایل‌های مشترک برای تمام کارت‌ها (آیکون راست، متن وسط)
    const cardStyle = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem 1rem',
        minHeight: '110px',
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        border: '1px solid #e2e8f0',
    };

    const iconWrapperStyle = (bgColor) => ({
        position: 'absolute',
        right: '1.5rem', // قرارگیری در سمت راست
        top: '50%',
        transform: 'translateY(-50%)', // وسط چین عمودی
        background: bgColor,
        width: '55px',
        height: '55px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    });

    const contentStyle = {
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        paddingRight: '4.5rem', // فضا برای آیکون سمت راست
        paddingLeft: '1rem',
    };

    // استایل مشترک برای عنوان‌ها برای جلوگیری از شکستن خط
    const titleStyle = {
        margin: 0,
        marginBottom: '0.5rem',
        color: '#64748b',
        fontWeight: 'normal',
        fontSize: '0.9rem',
        whiteSpace: 'nowrap' // 👈 این خط باعث می‌شود متن در یک خط بماند
    };

    return (
        <div className="company-dashboard" style={{ direction: 'rtl', display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: 0, paddingTop: 0 }}>

            {/* عنوان اصلی صفحه */}
            <div>
                <h1 style={{ fontSize: '1.5rem', color: '#1e293b', margin: 0 }}>داشبورد مدیریتی</h1>
            </div>

            {/* ردیف اول: وضعیت شرکت و شناسه ملی */}
            <div className="dashboard-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>

                <div className="stat-card" style={cardStyle}>
                    <div style={iconWrapperStyle(profile?.is_verified ? '#ecfdf5' : '#fffbeb')}>
                        <i className={`fas ${profile?.is_verified ? 'fa-check-circle' : 'fa-clock'}`} style={{ color: profile?.is_verified ? '#10b981' : '#f59e0b', fontSize: '1.5rem' }}></i>
                    </div>
                    <div style={contentStyle}>
                        <h4 style={titleStyle}>وضعیت شرکت</h4>
                        <p style={{ margin: 0, color: profile?.is_verified ? '#10b981' : '#f59e0b', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            {profile?.is_verified ? 'تایید شده' : 'در انتظار تایید'}
                        </p>
                    </div>
                </div>

                <div className="stat-card" style={cardStyle}>
                    <div style={iconWrapperStyle('#eff6ff')}>
                        <i className="fas fa-id-card" style={{ color: '#2563eb', fontSize: '1.5rem' }}></i>
                    </div>
                    <div style={contentStyle}>
                        <h4 style={titleStyle}>شناسه ملی</h4>
                        <p style={{ margin: 0, direction: 'ltr', fontSize: '1.1rem', fontWeight: 'bold', color: '#1e293b' }}>
                            {toPersianDigits(profile?.registration_number || 'ثبت نشده')}
                        </p>
                    </div>
                </div>

            </div>

            {/* عنوان بخش قراردادها */}
            <div style={{ marginTop: '1rem' }}>
                <h2 style={{ fontSize: '1.2rem', color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FileText size={20} color="#64748b" />
                    وضعیت قراردادها
                </h2>
            </div>

            {/* ردیف دوم: کارت‌های آماری قراردادها */}
            <div className="dashboard-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem' }}>

                {/* ۱. کل قراردادها */}
                <div className="stat-card" style={cardStyle}>
                    <div style={iconWrapperStyle('#eff6ff')}>
                        <FileText size={24} color="#2563eb" />
                    </div>
                    <div style={contentStyle}>
                        <h4 style={titleStyle}>قراردادهای خرید گروهی</h4>
                        <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', color: '#1e293b' }}>{fmt(stats?.contracts_count)} قرارداد</p>
                    </div>
                </div>

                {/* ۲. پرسنل خریدار */}
                <div className="stat-card" style={cardStyle}>
                    <div style={iconWrapperStyle('#ecfdf5')}>
                        <ShoppingBag size={24} color="#10b981" />
                    </div>
                    <div style={contentStyle}>
                        <h4 style={titleStyle}>پرسنل خریدار</h4>
                        <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', color: '#1e293b' }}>{fmt(stats?.purchasing_employees)} نفر</p>
                    </div>
                </div>

                {/* ۳. حجم خرید سازمانی */}
                <div className="stat-card" style={cardStyle}>
                    <div style={iconWrapperStyle('#f5f3ff')}>
                        <Users size={24} color="#8b5cf6" />
                    </div>
                    <div style={contentStyle}>
                        <h4 style={titleStyle}>حجم کل خرید سازمانی</h4>
                        <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', color: '#1e293b' }}>
                            {fmt(stats?.total_purchases)} <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#64748b' }}>ریال</span>
                        </p>
                    </div>
                </div>

                {/* ۴. پرسنل فعال */}
                <div className="stat-card" style={cardStyle}>
                    <div style={iconWrapperStyle('#fffbeb')}>
                        <UserCheck size={24} color="#f59e0b" />
                    </div>
                    <div style={contentStyle}>
                        <h4 style={titleStyle}>پرسنل فعال</h4>
                        <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', color: '#1e293b' }}>{fmt(stats?.active_employees)} نفر</p>
                    </div>
                </div>

            </div>

        </div>
    );
}